import { useRef } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type { GetBuyerLeadDrawerQuery } from '@graphql/__generated__/graphql';
import { BuyerLeadBrochureCard } from '@modules/_shared/components/buyer-lead/BuyerLeadBrochureCard';
import { BuyerLeadEnquiryCard } from '@modules/_shared/components/buyer-lead/BuyerLeadEnquiryCard';
import { BuyerLeadFeedV2 } from '@modules/_shared/components/buyer-lead/BuyerLeadFeedV2';
import { BuyerLeadOfferCard } from '@modules/_shared/components/buyer-lead/BuyerLeadOfferCard';
import { BuyerLeadOtherListingsCard } from '@modules/_shared/components/buyer-lead/BuyerLeadOtherListingsCard';
import { BuyerLeadStatusCard } from '@modules/_shared/components/buyer-lead/BuyerLeadStatusCard';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { ListingCardSnippet } from '@modules/_shared/components/ListingCardSnippet';
import {
  CONTENT_HEIGHT,
  TwoPaneLayout,
} from '@modules/_shared/components/TwoPaneLayout';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { canEditListing } from '@modules/_shared/utils/listingPermissions';
import { UserPropertySearchCard } from '@modules/users/user-overview/components/UserPropertySearchCard';
import { useLocale } from '@shared/hooks/locale';
import { getUUID } from '@shared/utils/global-id';

const GET_BUYER_LEAD_DRAWER = gql(/* GraphQL */ `
  query GetBuyerLeadDrawer($id: uuid!) {
    buyer_leads_by_pk(id: $id) {
      id
      ...BuyerLeadStatusCard_buyerLead
      mls_share {
        id
        status
        team_id
      }
      lot {
        id
        broker_id
        broker {
          id
          active_teams_users: teams_users(
            where: { deactivated: { _eq: false } }
          ) {
            team_id
            team {
              id
              lots_member_access
            }
          }
        }
        ...ListingCardSnippet
      }
    }
  }
`);

export const BuyerLeadEditDrawer: React.FC<{
  onClose: () => void;
  showListingCard?: boolean;
  showOtherListings?: boolean;
  offerInOverlay?: boolean;
  readOnly?: boolean;
}> = ({
  onClose,
  showListingCard,
  showOtherListings,
  offerInOverlay,
  readOnly = false,
}) => {
  const { t } = useLocale();
  const { buyerLeadId } = useParams();
  const id = buyerLeadId != null ? getUUID(buyerLeadId) : null;

  return (
    <Drawer onClose={onClose} title={t('Buyer lead')} maxWidth={1100}>
      <BuyerLeadDrawerDetails
        buyerLeadId={id}
        showListingCard={showListingCard}
        showOtherListings={showOtherListings}
        offerInOverlay={offerInOverlay}
        readOnly={readOnly}
      />
    </Drawer>
  );
};

export const BuyerLeadDrawerDetails: React.FC<{
  buyerLeadId: string | null;
  enquiryId?: string | null;
  showListingCard?: boolean;
  showOtherListings?: boolean;
  offerInOverlay?: boolean;
  readOnly?: boolean;
}> = ({
  buyerLeadId,
  enquiryId,
  showListingCard,
  showOtherListings,
  offerInOverlay,
  readOnly,
}) => {
  const { data, error, loading } = useQuery(GET_BUYER_LEAD_DRAWER, {
    variables: { id: buyerLeadId ?? '' },
    skip: buyerLeadId == null,
  });

  return (
    <BuyerLeadDrawerBody
      loading={loading}
      error={error}
      buyerLead={data?.buyer_leads_by_pk}
      enquiryId={enquiryId}
      showListingCard={showListingCard}
      showOtherListings={showOtherListings}
      offerInOverlay={offerInOverlay}
      readOnly={readOnly}
    />
  );
};

const BuyerLeadDrawerBody: React.FC<{
  loading: boolean;
  error?: ErrorLike;
  buyerLead?: BuyerLead | null;
  enquiryId?: string | null;
  showListingCard?: boolean;
  showOtherListings?: boolean;
  offerInOverlay?: boolean;
  readOnly?: boolean;
}> = ({
  loading,
  error,
  buyerLead,
  enquiryId,
  showListingCard,
  showOtherListings,
  offerInOverlay,
  readOnly,
}) => {
  const { t } = useLocale();

  if (loading) {
    return <LinearProgress />;
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }
  if (buyerLead == null) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('Lead not found')}
      </Alert>
    );
  }
  return (
    <BuyerLeadDrawerContent
      buyerLead={buyerLead}
      enquiryId={enquiryId}
      showListingCard={showListingCard}
      showOtherListings={showOtherListings}
      offerInOverlay={offerInOverlay}
      readOnly={readOnly}
    />
  );
};

type BuyerLead = NonNullable<GetBuyerLeadDrawerQuery['buyer_leads_by_pk']>;

const BuyerLeadDrawerContent: React.FC<{
  buyerLead: BuyerLead;
  enquiryId?: string | null;
  showListingCard?: boolean;
  showOtherListings?: boolean;
  offerInOverlay?: boolean;
  readOnly?: boolean;
}> = ({
  buyerLead,
  enquiryId,
  showListingCard,
  showOtherListings = true,
  offerInOverlay,
  readOnly = false,
}) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const feedScrollRef = useRef<HTMLDivElement>(null);

  const mlsShare = buyerLead.mls_share;
  const canEditViaMlsShare =
    mlsShare?.status === 'shared' &&
    (me?.teams ?? []).some(team => team.team_id === mlsShare.team_id);
  const canEdit =
    !readOnly &&
    (me?.is_admin === true ||
      canEditListing(me, buyerLead.lot) ||
      canEditViaMlsShare);

  const lotId = buyerLead.lot?.id ?? null;

  const cards = (
    <>
      <BuyerLeadStatusCard buyerLead={buyerLead} canEdit={canEdit} />

      <BuyerLeadEnquiryCard
        buyerLeadId={buyerLead.id}
        enquiryId={enquiryId}
        lotId={lotId}
        canEdit={canEdit}
      />

      <BuyerLeadBrochureCard lotId={lotId} userId={buyerLead.user.id} />

      {buyerLead.lot != null && (
        <BuyerLeadOfferCard
          lotId={buyerLead.lot.id}
          buyerId={buyerLead.user.id}
          createInOverlay={offerInOverlay}
          readOnly={!canEdit}
        />
      )}

      <UserPropertySearchCard userId={buyerLead.user.id} readOnly={!canEdit} />

      {showOtherListings && (
        <BuyerLeadOtherListingsCard
          userId={buyerLead.user.id}
          currentBuyerLeadId={buyerLead.id}
          readOnly={!canEdit}
        />
      )}

      {showListingCard !== false && buyerLead.lot != null && (
        <ListingAccordion title={t('Listing')} defaultExpanded disablePadding>
          <ListingCardSnippet
            listing={buyerLead.lot}
            link={`/listings/${buyerLead.lot.id}/buyers/${buyerLead.id}`}
            orientation="vertical"
            noElevation
          />
        </ListingAccordion>
      )}
    </>
  );

  const feed = (
    <BuyerLeadFeedV2
      buyerLeadId={buyerLead.id}
      phoneNumber={buyerLead.user.phone_numbers[0]?.number}
      readOnly={!canEdit}
    />
  );

  if (isMobile) {
    return (
      <Box
        ref={feedScrollRef}
        sx={{ overflowY: 'auto', height: CONTENT_HEIGHT, p: 2 }}
      >
        {cards}
        <Box sx={{ mt: 2 }}>{feed}</Box>
      </Box>
    );
  }

  return (
    <TwoPaneLayout
      leftPane={cards}
      rightPane={feed}
      rightPaneRef={feedScrollRef}
    />
  );
};
